/**
 * Live Chat Widget Component
 * Main chat widget container that manages all chat functionality
 *
 * Accessibility:
 * - Complementary landmark role
 * - Error boundary with recovery guidance
 * - Proper ARIA labeling throughout
 * - Respects reduced motion preferences
 */

import React, { useEffect, Component, ErrorInfo, ReactNode, useRef, useCallback } from 'react';
import { AnimatePresence } from 'motion/react';
import { ChatButton } from './ChatButton';
import { ChatWindow } from './ChatWindow';
import { ChatHistory } from './ChatHistory';
import { RateLimitModal } from './RateLimitModal';
import { RateLimitWarning } from './RateLimitWarning';
import { useChatWidget } from '../hooks/useChatWidget';
import { useChatHistory } from '../hooks/useChatHistory';
import { useChatRateLimit } from '../hooks/useChatRateLimit';
import { registerChatOpenListener, unregisterChatOpenListener } from '../utils/chatDispatcher';
import { cn } from '../../../../lib/utils';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface LiveChatWidgetProps {
  className?: string;
  chatbotName?: string;
}

/**
 * Error Boundary for Chat Window
 * Prevents chat window errors from crashing the entire widget
 * Shows actionable error message with recovery options
 */
class ChatWindowErrorBoundary extends Component<
  { children: ReactNode; onClose: () => void },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode; onClose: () => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error for debugging
    console.error('Chat window error:', error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className={cn(
            // Desktop: Full-height right sidebar (matches ChatWindow)
            'fixed right-0 top-0 bottom-0 z-50',
            'w-[480px] max-w-[90vw]',
            'h-screen h-dvh',
            // Mobile: full screen
            'max-md:inset-0',
            'max-md:w-full max-md:max-w-full',
            'bg-white dark:bg-gray-800 rounded-l-2xl max-md:rounded-none',
            'shadow-2xl border-l border-gray-200 dark:border-gray-700 max-md:border-0',
            'flex flex-col'
          )}
          role="alertdialog"
          aria-labelledby="chat-error-title"
          aria-describedby="chat-error-description"
        >
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center max-w-xs">
              <div
                className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4"
                aria-hidden="true"
              >
                <AlertTriangle className="w-7 h-7 text-amber-600 dark:text-amber-400" />
              </div>
              <h2
                id="chat-error-title"
                className="text-lg font-semibold text-gray-900 dark:text-white mb-2"
              >
                Something went wrong
              </h2>
              <p
                id="chat-error-description"
                className="text-sm text-gray-600 dark:text-gray-400 mb-6 leading-relaxed"
              >
                The chat encountered an unexpected error. You can try again or close the chat.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={this.handleRetry}
                  className={cn(
                    'w-full px-4 py-3 rounded-xl font-medium',
                    'min-h-[48px]',
                    'bg-emerald-600 hover:bg-emerald-700 text-white',
                    'transition-colors duration-150',
                    'focus:outline-hidden focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2',
                    'flex items-center justify-center gap-2'
                  )}
                  type="button"
                >
                  <RefreshCw className="w-4 h-4" aria-hidden="true" />
                  Try again
                </button>
                <button
                  onClick={() => {
                    this.setState({ hasError: false, error: null });
                    this.props.onClose();
                  }}
                  className={cn(
                    'w-full px-4 py-3 rounded-xl font-medium',
                    'min-h-[48px]',
                    'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200',
                    'hover:bg-gray-200 dark:hover:bg-gray-600',
                    'transition-colors duration-150',
                    'focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2'
                  )}
                  type="button"
                >
                  Close chat
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Main live chat widget component
 * Manages floating button, chat window, and history panel
 */
export const LiveChatWidget: React.FC<LiveChatWidgetProps> = ({
  className,
  chatbotName = 'Ask Hajra'
}) => {
  const {
    widgetState,
    chatState,
    messages,
    authState,
    isStreaming,
    showTypingIndicator,
    rateLimit,
    queuedMessagesCount,
    rateLimitRetryAfter,
    isLoadingConversation,
    loadingConversationId,
    showRateLimitModal,
    rateLimitType,
    handleSuggestionClick,
    getSuggestionsForMessage,
    rateLimitError,
    openChat,
    closeChat,
    toggleHistory,
    sendMessage,
    startNewConversation,
    loadConversation,
    continueAsGuest,
    closeRateLimitModal,
    // Message action handlers
    submitFeedback,
    regenerateMessage,
    canRegenerateMessage,
    messageFeedback
  } = useChatWidget();

  // Message action handlers (convert boolean to FeedbackType)
  const handleFeedback = useCallback((messageId: string, isPositive: boolean) => {
    submitFeedback(messageId, isPositive ? 'positive' : 'negative');
  }, [submitFeedback]);

  const handleRegenerate = useCallback((messageId: string) => {
    regenerateMessage(messageId);
  }, [regenerateMessage]);

  // Get feedback state for a message
  // Check ref first (includes optimistic updates), then fall back to message's embedded feedback
  const getMessageFeedback = useCallback((messageId: string): 'positive' | 'negative' | null => {
    // Check if ref has an entry for this message (includes optimistic updates)
    if (messageFeedback.has(messageId)) {
      const refFeedback = messageFeedback.get(messageId);
      // null = explicitly removed (toggle off), return null
      // 'positive'/'negative' = active feedback
      return refFeedback ?? null;
    }

    // Not in ref - fall back to message's embedded feedback (from API response)
    const message = messages.find(m => m.id === messageId);
    if (message?.feedback?.feedbackType) {
      return message.feedback.feedbackType;
    }

    return null;
  }, [messageFeedback, messages]);

  // Rate limit hook for enhanced state management
  const { rateLimitState, updateRateLimit } = useChatRateLimit();
  const previousRemainingRef = useRef<number | null>(null);

  // Update rate limit when prop changes
  useEffect(() => {
    updateRateLimit(rateLimit);
    if (rateLimit) {
      previousRemainingRef.current = rateLimit.remaining;
    }
  }, [rateLimit, updateRateLimit]);

  const {
    conversations,
    deleteConversation,
    hasMore,
    loadMore,
    isLoading
  } = useChatHistory();

  const isOpen = widgetState === 'open';
  const showHistory = widgetState === 'history';
  const isSending = chatState === 'sending' || chatState === 'streaming';

  // Register openChat function for external access
  useEffect(() => {
    registerChatOpenListener(openChat);
    return () => {
      unregisterChatOpenListener();
    };
  }, [openChat]);

  // Auto-reopen chat after successful authentication
  useEffect(() => {
    const shouldReopenChat = sessionStorage.getItem('amalq_reopen_chat') === 'true';

    if (shouldReopenChat) {
      sessionStorage.removeItem('amalq_reopen_chat');

      const timer = setTimeout(() => {
        if (authState === 'authenticated') {
          openChat();
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [authState, openChat]);

  return (
    <div
      className={cn('fixed inset-0 pointer-events-none z-50', className)}
      role="complementary"
      aria-label="Hajra chat assistant"
    >
      {/* Floating Button */}
      <ChatButton
        onClick={openChat}
        isOpen={isOpen}
      />

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && !showHistory && (
          <div className="pointer-events-auto">
            <ChatWindowErrorBoundary onClose={closeChat}>
              <ChatWindow
                messages={messages}
                onClose={closeChat}
                onToggleHistory={toggleHistory}
                onSendMessage={sendMessage}
                onNewChat={startNewConversation}
                onGuestContinue={continueAsGuest}
                isOpen={isOpen}
                isSending={isSending}
                isStreaming={isStreaming}
                showTypingIndicator={showTypingIndicator}
                isLoadingConversation={isLoadingConversation}
                authState={authState}
                chatbotName={chatbotName}
                rateLimit={rateLimit}
                queuedMessagesCount={queuedMessagesCount}
                rateLimitRetryAfter={rateLimitRetryAfter}
                handleSuggestionClick={handleSuggestionClick}
                getSuggestionsForMessage={getSuggestionsForMessage}
                onFeedback={handleFeedback}
                onRegenerate={handleRegenerate}
                canRegenerateMessage={canRegenerateMessage}
                getMessageFeedback={getMessageFeedback}
              />
            </ChatWindowErrorBoundary>
          </div>
        )}
      </AnimatePresence>

      {/* History Panel - Full-height sidebar */}
      <AnimatePresence>
        {showHistory && (
          <div className="pointer-events-auto fixed right-0 top-0 bottom-0 z-50 w-[480px] max-w-[90vw] h-screen h-dvh max-md:inset-0 max-md:w-full max-md:max-w-full max-md:rounded-none">
            <ChatHistory
              conversations={conversations}
              onSelectConversation={loadConversation}
              onDeleteConversation={deleteConversation}
              onClose={toggleHistory}
              hasMore={hasMore}
              onLoadMore={loadMore}
              isLoadingMore={isLoading}
              loadingConversationId={loadingConversationId}
            />
          </div>
        )}
      </AnimatePresence>

      {/* Rate Limit Modal */}
      <RateLimitModal
        isOpen={showRateLimitModal}
        onClose={closeRateLimitModal}
        rateLimitState={rateLimitState}
        errorMessage={rateLimitError || undefined}
        retryAfter={rateLimitRetryAfter}
        limitType={rateLimitType}
      />

      {/* Progressive Rate Limit Warnings */}
      {rateLimitState && (
        <RateLimitWarning
          rateLimitState={rateLimitState}
          previousRemaining={previousRemainingRef.current}
        />
      )}
    </div>
  );
};

export default LiveChatWidget;
